import React from 'react';
import { ContentBlock, ContentBlockProps } from '@jbhi-fi/components/src/ContentBlock';
import { PresentationProductCarousel, ProductCarousel } from '@jbhi-fi/components/src/ProductCarousel/ProductCarousel';
import { ContentBanner } from '../ContentBanner/ContentBanner';
import { PresentationVideoCardCarousel } from '@jbhi-fi/components/src/ContentCard/VideoCardCarousel/VideoCardCarousel';
import { mergeResponsiveValues, ResponsiveValue } from '../../utils/responsiveValue';
import { CardProvider } from '../../hooks/useCards';
import { CardGrid } from '@jbhi-fi/components/src/ContentCard';
import { CardGridVariant } from '@jbhi-fi/components/src/ContentCard/CardGrid/CardGrid';
import { HeroBlock } from '@jbhi-fi/components/src/HeroBlock';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { HeadingDivider, HeadingDividerVariant } from '@jbhi-fi/components/src/HeadingDivider/HeadingDivider';
import { useResponsiveValue } from '@jbhi-fi/components/src/hooks/useResponsiveValue';
import { styles } from './Section.css';
import { spacing } from '@jbhi-fi/lanyard-ui/themes';

export enum ElementType {
  cardProvider = 'cardProvider',
  cardGrid = 'cardGrid',
  cardGridWithPrimary = 'cardGridWithPrimary',
  heroBlock = 'heroBlock',
  productCarousel = 'productCarousel',
  countPresentationProductCarousel = 'countPresentationProductCarousel',
  presentationProductCarousel = 'presentationProductCarousel',
  presentationVideoCarousel = 'presentationVideoCarousel',
  cardCarousel = 'cardCarousel',
  contentBanner = 'contentBanner',
}

export type SectionElementProps<T> = {
  type: ElementType;
  props: T;
};

export type SectionProps = {
  title?: string;
  href?: string;
  shouldOpenInNewWindow?: boolean;
  spaceBetweenElements?: Partial<ResponsiveValue<number>> | number;
  titlePadding?: Partial<ResponsiveValue<number>> | number;
  titleColor?: 'white' | 'black';
  titleVariant?: HeadingDividerVariant;
  contentBlockProps?: Omit<ContentBlockProps, 'children'>;
  elements: Array<SectionElementProps<any>>;
};

const elementMap: Record<
  ElementType,
  { component: React.FunctionComponent<any>; defaultProps: { [key: string]: any } }
> = {
  [ElementType.cardProvider]: { component: CardProvider, defaultProps: {} },
  [ElementType.cardGrid]: {
    component: CardGrid,
    defaultProps: { variant: CardGridVariant.default, shouldShowMobileCarousel: true },
  },
  [ElementType.cardGridWithPrimary]: { component: CardGrid, defaultProps: { variant: CardGridVariant.withPrimary } },
  [ElementType.heroBlock]: { component: HeroBlock, defaultProps: {} },
  [ElementType.productCarousel]: { component: ProductCarousel, defaultProps: {} },
  [ElementType.countPresentationProductCarousel]: {
    component: PresentationProductCarousel,
    defaultProps: {},
  },
  [ElementType.presentationProductCarousel]: {
    component: PresentationProductCarousel,
    defaultProps: {},
  },
  [ElementType.presentationVideoCarousel]: { component: PresentationVideoCardCarousel, defaultProps: {} },
  [ElementType.cardCarousel]: {
    component: Carousel,
    defaultProps: {
      fullHeightItems: true,
      showProgressBar: true,
      showArrows: true,
      spaceBetween: 10,
    },
  },
  [ElementType.contentBanner]: { component: ContentBanner, defaultProps: {} },
};

export const Section = ({
  title,
  href,
  shouldOpenInNewWindow,
  spaceBetweenElements,
  titlePadding,
  titleVariant,
  titleColor,
  contentBlockProps = {},
  elements,
}: SectionProps) => {
  const titlePaddingResponsive = mergeResponsiveValues<number>({ desktopLg: 9, desktop: 9, mobile: 5 }, titlePadding);
  const titlePaddingFinal = useResponsiveValue<number>({
    xs: titlePaddingResponsive.mobile,
    md: titlePaddingResponsive.desktop,
    lg: titlePaddingResponsive.desktopLg,
  });

  const spaceBetweenElementsResponsive = mergeResponsiveValues<number>(
    { desktopLg: 10, desktop: 10, mobile: 7.5 },
    spaceBetweenElements
  );
  const spaceBetweenElementsFinal = useResponsiveValue<number>({
    xs: spaceBetweenElementsResponsive.mobile,
    md: spaceBetweenElementsResponsive.desktop,
    lg: spaceBetweenElementsResponsive.desktopLg,
  });

  if (!elements?.length) return null;

  return (
    <ContentBlock paddingTop={15} {...contentBlockProps}>
      {title ? (
        <div style={{ paddingBottom: spacing(titlePaddingFinal) }}>
          <HeadingDivider
            heading={title}
            href={href}
            shouldOpenInNewWindow={shouldOpenInNewWindow}
            textColor={titleColor}
            variant={titleVariant}
          />
        </div>
      ) : null}
      <div className={styles.elementsContainer} style={{ gap: spacing(spaceBetweenElementsFinal) }}>
        {elements.map((element, i) => {
          const { component: Component, defaultProps } = elementMap[element.type];
          return <Component key={i} {...defaultProps} {...element.props} />;
        })}
      </div>
    </ContentBlock>
  );
};
